import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db, COLLECTIONS, isTeacher } from '../../utils/firebase';
import { useAuth } from '../../contexts/AuthContext';
import CloseIcon from '../icons/CloseIcon';
import DropdownButton from './DropdownButton';
import InputField from './InputField';
import { School } from '../../utils/firebase';
import { doc, getDoc } from 'firebase/firestore';
import { Teacher } from '../../utils/firebase';

const fetchSchoolInfo = async (teacher: Teacher) => {
  if (!teacher || !isTeacher(teacher)) return;
  const schoolDoc = await getDoc(doc(db, COLLECTIONS.SCHOOLS, teacher.schoolId));
  return schoolDoc.data() as School;
};

interface AddNewClassDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (data: {
    grade: number;
    class: number;
    code?: string;
  }) => void;
}

const AddNewClassDialog: React.FC<AddNewClassDialogProps> = ({
  isOpen,
  onClose,
  onAdd
}) => {
  const { currentUser } = useAuth();
  const [selectedGrade, setSelectedGrade] = useState<string>('');
  const [selectedClass, setSelectedClass] = useState<string>('');
  const [generatedCode, setGeneratedCode] = useState<string>('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [schoolInfo, setSchoolInfo] = useState<School | undefined>(undefined);

  useEffect(() => {
    if (!currentUser || !isTeacher(currentUser as Teacher)) return;
    fetchSchoolInfo(currentUser as Teacher).then((schoolInfo) => {
      if (schoolInfo) {
        setSchoolInfo(schoolInfo);
      }
    });
  }, [currentUser]);

  // 학년 옵션 (1-3학년)
  const gradeOptions = [
    { value: '1', label: '1학년' },
    { value: '2', label: '2학년' },
    { value: '3', label: '3학년' }
  ];
  
  // 반 옵션 (1-10반)
  const classOptions = Array.from({ length: schoolInfo?.maxClassCount || 10 }, (_, i) => ({
    value: String(i + 1),
    label: `${i + 1}반`
  }));

  // 다이얼로그가 열릴 때 초기화
  useEffect(() => {
    if (isOpen) {
      setSelectedGrade('');
      setSelectedClass('');
      setGeneratedCode('');
    }
  }, [isOpen]);

  // 학년·반 모두 선택 시 코드 자동 생성 (코드가 무조건 생성된 채로 담당 교실 생성)
  useEffect(() => {
    if (!isOpen || !selectedGrade || !selectedClass || !currentUser || !isTeacher(currentUser as Teacher)) return;
    if (!(currentUser as Teacher).subjectId) return;

    let cancelled = false;
    setIsGenerating(true);
    getOrCreateCode()
      .then((code) => {
        if (!cancelled && code) setGeneratedCode(code);
      })
      .catch((err) => {
        if (!cancelled) console.error('담당 교실 코드 자동 생성 오류:', err);
      })
      .finally(() => {
        if (!cancelled) setIsGenerating(false);
      });
    return () => { cancelled = true; };
  }, [isOpen, selectedGrade, selectedClass]);

  // 코드 조회 또는 생성 (GameStudentManagement와 동일한 로직)
  const getOrCreateCode = async (): Promise<string | null> => {
    const teacher = currentUser as Teacher;
    if (!teacher?.subjectId) return null;

    const subjectCodeRef = collection(db, COLLECTIONS.SUBJECT_CODES);
    const grade = parseInt(selectedGrade);
    const classNum = parseInt(selectedClass);

    // 기존 코드 조회 (schoolId + subjectId + grade + class)
    const existingQuery = query(
      subjectCodeRef,
      where('schoolId', '==', teacher.schoolId),
      where('subjectId', '==', teacher.subjectId),
      where('grade', '==', grade),
      where('class', '==', classNum)
    );
    const existingSnapshot = await getDocs(existingQuery);
    if (!existingSnapshot.empty) {
      return existingSnapshot.docs[0].data().code;
    }

    // 새 코드 생성 (6자리, GameStudentManagement와 동일한 형식)
    for (let attempts = 0; attempts < 10; attempts++) {
      const newCode = Math.random().toString(36).substring(2, 8).toUpperCase();
      const duplicateQuery = query(
        subjectCodeRef,
        where('code', '==', newCode),
        where('schoolId', '==', teacher.schoolId)
      );
      const duplicateSnapshot = await getDocs(duplicateQuery);
      if (duplicateSnapshot.empty) {
        return newCode;
      }
    }
    return null;
  };

  const handleGenerateCode = async () => {
    if (!selectedGrade || !selectedClass) {
      alert('학년과 반을 먼저 선택해주세요.');
      return;
    }

    if (!currentUser) {
      alert('로그인이 필요합니다.');
      return;
    }

    if (!(currentUser as Teacher).subjectId) {
      alert('교과 정보가 없습니다.');
      return;
    }

    setIsGenerating(true);
    try {
      const code = await getOrCreateCode();
      if (code) {
        setGeneratedCode(code);
      } else {
        alert('코드 생성에 실패했습니다. 다시 시도해주세요.');
      }
    } catch (error) {
      console.error('코드 생성 오류:', error);
      alert('코드 생성 중 오류가 발생했습니다.');
    } finally {
      setIsGenerating(false);
    }
  };

  // 추가 버튼 클릭 (코드는 반드시 이미 생성된 상태여야 함)
  const handleAdd = () => {
    if (!selectedGrade || !selectedClass) {
      alert('학년과 반을 선택해주세요.');
      return;
    }

    if (!currentUser || !(currentUser as Teacher).subjectId) {
      alert('교과 정보가 없습니다.');
      return;
    }

    if (!generatedCode) {
      alert('코드가 생성될 때까지 잠시 기다려주세요. 생성이 완료된 후 추가할 수 있습니다.');
      return;
    }

    onAdd({
      grade: parseInt(selectedGrade),
      class: parseInt(selectedClass),
      code: generatedCode
    });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: 'Pretendard',
        zIndex: 1000
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        style={{
          width: '450px',
          height: '509px',
          position: 'relative',
          background: 'white',
          boxShadow: '0px 8px 16px rgba(0, 0, 0, 0.12)',
          overflow: 'hidden',
          borderRadius: 16,
          margin: '0 auto',
          padding: '24px 28px',
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
          gap: '77px'
        }}
        // 모달 내부 클릭 시 외부(onClick) 이벤트 버블링 방지
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            width: '100%',
            height: '339px',
            flexDirection: 'column',
            justifyContent: 'flex-start',
            alignItems: 'flex-start',
            gap: '40px',
            display: 'flex'
          }}
        >
          {/* 제목 및 닫기 버튼 */}
          <div
            style={{
              width: '100%',
              height: '32px',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 8,
              display: 'flex',
              flexShrink: 0 // flex 아이템이 공간 부족 시 줄어들지 않도록 설정
            }}
          >
            <div
              style={{
                // flex-grow: 1, flex-shrink: 1, flex-basis: 0 (자동으로 남은 공간을 차지하도록 설정)
                flex: '1 1 0',
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <div
                style={{
                  color: 'var(--Color-text-normal, #161616)',
                  fontSize: 20,
                  fontFamily: 'Pretendard',
                  fontWeight: '600',
                  lineHeight: '30px',
                  wordWrap: 'break-word'
                }}
              >
                담당 교실 만들기
              </div>
            </div>
            <div
              style={{
                justifyContent: 'flex-start',
                alignItems: 'flex-start',
                display: 'flex',
                flexShrink: 0
              }}
            >
              <button
                onClick={onClose}
                style={{
                  padding: 4,
                  borderRadius: 8,
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: 10,
                  display: 'flex',
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer'
                }}
              >
                <div
                  style={{
                    width: 24,
                    height: 24,
                    position: 'relative',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: 10,
                    display: 'inline-flex'
                  }}
                >
                  <CloseIcon width={13.20} height={13.20} color="#0D0D0D" />
                </div>
              </button>
            </div>
          </div>

          {/* 컨텐츠 영역 */}
          <div
            style={{
              width: '100%',
              height: '267px',
              flexDirection: 'column',
              justifyContent: 'flex-start',
              alignItems: 'flex-start',
              gap: '28px',
              display: 'flex'
            }}
          >
              {/* 담당 교실 섹션 */}
              <div
                style={{
                  width: '100%',
                  height: '117px',
                  flexDirection: 'column',
                  justifyContent: 'flex-start',
                  alignItems: 'flex-start',
                  gap: 20,
                  display: 'flex'
                }}
              >
                <div
                  style={{
                    width: '100%',
                    height: '49px',
                    flexDirection: 'column',
                    justifyContent: 'flex-start',
                    alignItems: 'flex-start',
                    gap: '4px',
                    display: 'flex'
                  }}
                >
                  <div
                    style={{
                      width: '100%',
                      height: '24px',
                      display: 'flex',
                      alignItems: 'center',
                      color: 'var(--Color-text-normal, #161616)',
                      fontSize: 16,
                      fontFamily: 'Pretendard',
                      fontWeight: '600',
                      lineHeight: '24px',
                      wordWrap: 'break-word'
                    }}
                  >
                    담당 교실
                  </div>
                  <div
                    style={{
                      width: '100%',
                      height: '21px',
                      display: 'flex',
                      alignItems: 'center',
                      color: 'var(--Color-text-assistive, #989898)',
                      fontSize: 14,
                      fontFamily: 'Pretendard',
                      fontWeight: '500',
                      lineHeight: '21px',
                      wordWrap: 'break-word'
                    }}
                  >
                    현재 담당하고 계신 학년과 반을 입력해주세요
                  </div>
                </div>
                {/* 학년/반 선택 */}
                <div
                  style={{
                    width: '100%',
                    height: '48px',
                    justifyContent: 'flex-start',
                    alignItems: 'center',
                    gap: '12px',
                    display: 'inline-flex'
                  }}
                >
                  {/* 학년 드롭다운 */}
                  <DropdownButton
                    options={gradeOptions}
                    value={selectedGrade}
                    onChange={(value) => setSelectedGrade(value)}
                    placeholder="학년"
                    width="115px"
                    height="48px"
                  />

                  {/* 반 드롭다운 */}
                  <DropdownButton
                    options={classOptions}
                    value={selectedClass}
                    onChange={(value) => setSelectedClass(value)}
                    placeholder="반"
                    width="115px"
                    height="48px"
                  />
                </div>
              </div>

              {/* 코드 생성 섹션 */}
              <div
                style={{
                  width: '100%',
                  height: '122px',
                  flexDirection: 'column',
                  justifyContent: 'flex-start',
                  alignItems: 'flex-start',
                  gap: '20px',
                  display: 'flex'
                }}
              >
                <div
                  style={{
                    width: '100%',
                    height: '49px',
                    flexDirection: 'column',
                    justifyContent: 'flex-start',
                    alignItems: 'flex-start',
                    gap: '4px',
                    display: 'flex'
                  }}
                >
                  <div
                    style={{
                      width: '100%',
                      height: '24px',
                      color: 'var(--Color-text-normal, #161616)',
                      fontSize: 16,
                      fontFamily: 'Pretendard',
                      fontWeight: '600',
                      lineHeight: '24px',
                      wordWrap: 'break-word',
                      display: 'flex',
                      alignItems: 'center'
                    }}
                  >
                    코드 생성하기
                  </div>
                  <div
                    style={{
                      width: '100%',
                      height: '21px',
                      color: 'var(--Color-text-assistive, #989898)',
                      fontSize: 14,
                      fontFamily: 'Pretendard',
                      fontWeight: '500',
                      lineHeight: 21,
                      wordWrap: 'break-word',
                      display: 'flex',
                      alignItems: 'center'
                    }}
                  >
                    이 코드를 사용하여 학생들이 교실에 등록할 수 있습니다.
                  </div>
                </div>
                <div
                  style={{
                    width: '100%',
                    height: '48px',
                    justifyContent: 'flex-start',
                    alignItems: 'center',
                    gap: '8px',
                    display: 'inline-flex'
                  }}
                >
                  <div style={{ 
                    flex: 1, 
                    height: '48px',
                    minHeight: '48px',
                    maxHeight: '48px',
                    overflow: 'hidden'
                  }}>
                    <InputField
                      value={generatedCode}
                      onChange={(value) => setGeneratedCode(value)}
                      placeholder="학년·반 선택 시 자동 생성됩니다"
                      width="100%"
                      height="48px"
                      size="medium"
                    />
                  </div>
                  <div
                    style={{
                      width: 104,
                      height: '48px',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      display: 'flex'
                    }}
                  >
                    <button
                      onClick={handleGenerateCode}
                      disabled={isGenerating}
                      style={{
                        width: '100%',
                        height: '48px',
                        paddingLeft: 20,
                        paddingRight: 20,
                        background: isGenerating 
                          ? 'var(--Color-text-minor, #BABABA)' 
                          : 'var(--Color-fill-brand-color, #FF6F0F)',
                        borderRadius: 12,
                        justifyContent: 'center',
                        alignItems: 'center',
                        gap: 8,
                        display: 'flex',
                        border: 'none',
                        cursor: isGenerating ? 'not-allowed' : 'pointer',
                        opacity: isGenerating ? 0.6 : 1
                      }}
                    >
                      <div
                        style={{
                          color: 'var(--Color-text-inverse, white)',
                          fontSize: 14,
                          fontFamily: 'Pretendard',
                          fontWeight: '500',
                          lineHeight: 21,
                          wordWrap: 'break-word',
                          height: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        {isGenerating ? '생성 중...' : '생성하기'}
                      </div>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* 하단 버튼 */}
          <div
            style={{
              width: '100%',
              height: '45px',
              justifyContent: 'flex-start',
              alignItems: 'center',
              gap: '8px',
              display: 'inline-flex',
              flexShrink: 0
            }}
          >
            <div
              style={{
                width: '124px',
                height: '45px',
                justifyContent: 'flex-start',
                alignItems: 'flex-start',
                display: 'flex'
              }}
            >
              <button
                onClick={onClose}
                style={{
                  flex: '1 1 0',
                  paddingLeft: 16,
                  paddingRight: 16,
                  paddingTop: 12,
                  paddingBottom: 12,
                  background: '#E9E9E9',
                  borderRadius: 8,
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: 4,
                  display: 'flex',
                  border: 'none',
                  cursor: 'pointer',
                  width: '100%',
                  height: '100%'
                }}
              >
                <div
                  style={{
                    color: 'var(--Color-text-normal, #161616)',
                    fontSize: 14,
                    fontFamily: 'Pretendard',
                    fontWeight: '500',
                    display: 'flex',
                    alignItems: 'center',
                    lineHeight: '21px',
                    wordWrap: 'break-word',
                    height: '100%'
                  }}
                >
                  취소
                </div>
              </button>
            </div>
            <div
              style={{
                width: 262,
                height: '45px',
                justifyContent: 'flex-start',
                alignItems: 'flex-start',
                display: 'flex'
              }}
            >
              <button
                onClick={handleAdd}
                disabled={!generatedCode || isGenerating}
                style={{
                  flex: '1 1 0',
                  paddingLeft: 16,
                  paddingRight: 16,
                  paddingTop: 12,
                  paddingBottom: 12,
                  background: generatedCode && !isGenerating
                    ? 'var(--Color-fill-normal, #293044)'
                    : 'var(--Color-text-minor, #BABABA)',
                  borderRadius: 8,
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: 4,
                  display: 'flex',
                  border: 'none',
                  cursor: generatedCode && !isGenerating ? 'pointer' : 'not-allowed',
                  width: '100%',
                  height: '100%',
                  opacity: generatedCode && !isGenerating ? 1 : 0.7
                }}
              >
                <div
                  style={{
                    color: 'var(--Color-text-inverse, white)',
                    fontSize: 14,
                    fontFamily: 'Pretendard',
                    fontWeight: '500',
                    lineHeight: 21,
                    wordWrap: 'break-word'
                  }}
                >
                  추가
                </div>
              </button>
            </div>
          </div>
        </div>
      </div>
  );
};

export default AddNewClassDialog;

